import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { message, Spin } from "antd";
import axios from "axios";
import { BASE_URL } from "../Config";

// ── Pricing table (mirrors backend plans.ts) ──────────────────────
const PLAN_PRICES_USD = {
  "basic-1": {
    20: { withoutLogo: 1, withLogo: 2 },
    50: { withoutLogo: 2, withLogo: 3 },
    100: { withoutLogo: 3, withLogo: 4 },
    200: { withoutLogo: 5, withLogo: 6 },
  },
  "basic-2": {
    20: { withoutLogo: 3, withLogo: 5 },
    50: { withoutLogo: 6, withLogo: 8 },
    100: { withoutLogo: 9, withLogo: 11 },
    200: { withoutLogo: 15, withLogo: 18 },
  },
  standard: {
    20: { withoutLogo: 5, withLogo: 8 },
    50: { withoutLogo: 10, withLogo: 13 },
    100: { withoutLogo: 15, withLogo: 18 },
    200: { withoutLogo: 30, withLogo: 35 },
  },
  premium: {
    20: { withoutLogo: 10, withLogo: 15 },
    50: { withoutLogo: 20, withLogo: 25 },
    100: { withoutLogo: 30, withLogo: 35 },
    200: { withoutLogo: 50, withLogo: 60 },
  },
};

const PLANS = [
  {
    key: "basic-1",
    label: "Basic - 1",
    duration: "1 Month",
    color: "#6366f1",
    light: "#eef2ff",
  },
  {
    key: "basic-2",
    label: "Basic - 2",
    duration: "3 Months",
    color: "#0ea5e9",
    light: "#e0f2fe",
  },
  {
    key: "standard",
    label: "Standard",
    duration: "6 Months",
    color: "#10b981",
    light: "#d1fae5",
  },
  {
    key: "premium",
    label: "Premium",
    duration: "1 Year",
    color: "#f59e0b",
    light: "#fef3c7",
  },
];

const MEDIA_TIERS = ["20", "50", "100", "200"];

const getPrice = (plan, tier, withLogo) =>
  PLAN_PRICES_USD[plan]?.[tier]?.[withLogo ? "withLogo" : "withoutLogo"] ?? 0;

// ── Load Razorpay script ──────────────────────────────────────────
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// ─────────────────────────────────────────────────────────────────
const PaymentModal = ({
  open,
  onClose,
  userId,
  userMediaLinkCount = 0,
  onSuccess,
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [withLogo, setWithLogo] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [paying, setPaying] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Determine user's media tier from their count
  const userTier =
    userMediaLinkCount <= 20
      ? "20"
      : userMediaLinkCount <= 50
        ? "50"
        : userMediaLinkCount <= 100
          ? "100"
          : "200";

  useEffect(() => {
    if (!open) return;
    // Fetch live exchange rate
    axios
      .get(`${BASE_URL}/payment/exchange-rate`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      })
      .then((res) => {
        if (res.data?.rate) setExchangeRate(res.data.rate);
      })
      .catch(() => setExchangeRate(84)); // fallback
  }, [open]);

  const handleClose = () => {
    setSelectedPlan(null);
    setWithLogo(false);
    setErrorMsg(null);
    onClose();
  };

  const handlePay = async () => {
    if (!selectedPlan) return message.warning("Please select a plan");
 if (!userId) return message.error("User session expired. Please log in again.");

    const loaded = await loadRazorpay();
    if (!loaded)
      return message.error(
        "Payment gateway failed to load. Check your connection.",
      );

    setPaying(true);
    try {
      const token = sessionStorage.getItem("token");
      const { data } = await axios.post(
        `${BASE_URL}/payment/create-order`,
        { plan: selectedPlan, mediaLinks: userTier, withLogo, userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!data.success) throw new Error(data.error);

      const plan = PLANS.find((p) => p.key === selectedPlan);

      const rzp = new window.Razorpay({
        key: data.data.key,
        amount: data.data.amount,
        currency: data.data.currency,
        order_id: data.data.orderId,
        name: "YogaRefer",
        description: `${plan?.label} Plan${withLogo ? " + Logo" : ""}`,
        theme: { color: plan?.color ?? "#6366f1" },
        handler: async (response) => {
          try {
            await axios.post(
              `${BASE_URL}/payment/verify`,
              {
                ...response,
                userId,
                plan: selectedPlan,
                mediaLinks: userTier,
                withLogo,
              },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            message.success(
              "Payment successful! Your plan has been activated.",
            );
            handleClose();
            onSuccess?.();
          } catch {
            message.error(
              "Payment verified but activation failed. Contact support.",
            );
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });

      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to initiate payment.";
      setErrorMsg(msg);
      setPaying(false);
    }
  };

  const selectedPriceUSD = selectedPlan
    ? getPrice(selectedPlan, userTier, withLogo)
    : null;
  const selectedPriceINR =
    selectedPriceUSD && exchangeRate
      ? Math.round(selectedPriceUSD * exchangeRate)
      : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 1000,
              backdropFilter: "blur(3px)",
            }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.88, y: 32 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 32 }}
            transition={{ duration: 0.26, ease: "easeOut" }}
           style={{
              position: "fixed",
              top: 10,
              left: 0,
              right: 0,
              margin: "0 auto",
              overscrollBehavior: "contain",
              zIndex: 1001,
              width: "calc(100vw - 24px)",
              maxWidth: 600,
              maxHeight: "95vh",
              overflowY: "auto",
              overflowX: "hidden",
              background: "#fff",
              borderRadius: 20,
              boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
              padding: "20px 16px 20px",
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#1a1a2e",
                }}
              >
                Extend Your Plan
              </h2>
              <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
                Your media tier: <strong>≤{userTier} links</strong>
                {exchangeRate && (
                  <span style={{ marginLeft: 8, color: "#9ca3af" }}>
                    · 1 USD = ₹{exchangeRate.toFixed(1)}
                  </span>
                )}
              </p>
            </div>

            {/* Logo Toggle */}
            <div
              onClick={() => {
                setWithLogo((v) => !v);
                setErrorMsg(null);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderRadius: 12,
                border: `2px solid ${withLogo ? "#6366f1" : "#e5e7eb"}`,
                background: withLogo ? "#eef2ff" : "#fafafa",
                cursor: "pointer",
                marginBottom: 20,
                transition: "all 0.2s",
                userSelect: "none",
              }}
            >
              {/* Toggle pill */}
              <div
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 11,
                  background: withLogo ? "#6366f1" : "#d1d5db",
                  position: "relative",
                  flexShrink: 0,
                  transition: "background 0.2s",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: withLogo ? 21 : 3,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: withLogo ? "#4f46e5" : "#374151",
                  }}
                >
                  Add Logo Watermark
                  {withLogo && (
                    <span
                      style={{ marginLeft: 6, fontSize: 12, color: "#6366f1" }}
                    >
                      ✓ Selected
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  150×150px logo added to all images · auto-removed on expiry
                </div>
              </div>
            </div>

            {/* Plan Cards */}
            <div
              style={{
                display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 10,
              marginBottom: 20,
              }}
            >
              {PLANS.map((plan) => {
                const priceUSD = getPrice(plan.key, userTier, withLogo);
                const priceINR = exchangeRate
                  ? Math.round(priceUSD * exchangeRate)
                  : null;
                const isSelected = selectedPlan === plan.key;

                return (
                  <motion.div
                    key={plan.key}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      setSelectedPlan(plan.key);
                      setErrorMsg(null);
                    }}
                    style={{
                      border: `2px solid ${isSelected ? plan.color : "#e5e7eb"}`,
                      borderRadius: 14,
                      padding: "16px 14px",
                      cursor: "pointer",
                      background: isSelected ? plan.light : "#fff",
                      transition: "all 0.18s",
                      position: "relative",
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 10,
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: plan.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          color: "#fff",
                          fontWeight: 700,
                        }}
                      >
                        ✓
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: plan.color,
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      {plan.label}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#374151",
                        marginBottom: 8,
                      }}
                    >
                      {plan.duration}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#1a1a2e",
                          lineHeight: 1,
                        }}
                      >
                        ${priceUSD}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        / ${getPrice(plan.key, userTier, !withLogo)}{" "}
                        {withLogo ? "without logo" : "with logo"}
                      </div>
                    </div>
                    {priceINR && (
                      <div
                        style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}
                      >
                        ≈ ₹{priceINR.toLocaleString("en-IN")}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Summary bar */}
            <AnimatePresence>
              {selectedPlan && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    background: "#f8fafc",
                    borderRadius: 12,
                    padding: "12px 16px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#374151",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {PLANS.find((p) => p.key === selectedPlan)?.label} ·{" "}
                    {PLANS.find((p) => p.key === selectedPlan)?.duration}
                    {withLogo && " · Logo"}
                  </span>
                  <span
                    style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}
                  >
                    ${selectedPriceUSD}
                    {selectedPriceINR && (
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: 12,
                          color: "#9ca3af",
                          marginLeft: 6,
                        }}
                      >
                        (₹{selectedPriceINR.toLocaleString("en-IN")})
                      </span>
                    )}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Error banner */}
            {errorMsg && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "12px 16px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1.2 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#dc2626",
                      marginBottom: 2,
                    }}
                  >
                    Extension Not Available
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#b91c1c", lineHeight: 1.5 }}
                  >
                    {errorMsg}
                  </div>
                </div>
                <button
                  onClick={() => setErrorMsg(null)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    color: "#dc2626",
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Footer buttons */}

            {/* Footer buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleClose}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 10,
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  color: "#6b7280",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePay}
                disabled={!selectedPlan || paying}
                style={{
                  flex: 2,
                  padding: "11px 0",
                  border: "none",
                  borderRadius: 10,
                  background: selectedPlan
                    ? PLANS.find((p) => p.key === selectedPlan)?.color
                    : "#d1d5db",
                  color: "#fff",
                  cursor: selectedPlan ? "pointer" : "not-allowed",
                  fontSize: 14,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 0.2s",
                }}
              >
                {paying ? (
                  <Spin
                    size="small"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                ) : null}
                {paying
                  ? "Processing…"
                  : `Pay ${selectedPriceUSD ? `$${selectedPriceUSD}` : ""}`}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;
